// src/components/onboarding/OnboardingModal.jsx
// Professional EdTech onboarding: Linear/Duolingo Max–style UI, OptionCard, split exam selection.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Trophy,
  ClipboardList,
  Calendar as CalendarIcon,
  History,
  Layers,
  ChevronLeft,
  CheckCircle2,
  Type,
  Headphones,
  PenLine,
  MessageCircle,
  Rocket
} from 'lucide-react';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import { useTranslation } from 'react-i18next';
import { soundManager, SOUNDS } from '../../utils/soundUtils';
import {
  PLACEMENT_QUESTIONS,
  getPlacementLevel,
  getWeakestSubject,
  getCorrectIndex,
  FIRST_ADVANCED_QUESTION_INDEX
} from './placementQuestions';
import PlacementDiagnosticSlide from './PlacementDiagnosticSlide';
import ExamModePicker from '../practice/ExamModePicker.jsx';
import { supportsPaper, getExamModePreference, setExamModePreference } from '../../utils/examMode';
import { trackOnboardingStepCompleted, trackPlanSelected, trackPricingView } from '../../utils/conversionEvents';
import { getLandingPageVariant } from '../../utils/experiments';
import { getPlanPriceLabel } from '../../config/plans';
import { useRegionalPricing } from '../../hooks/useRegionalPricing';

const SLIDE_COUNT = 26;
const ENDOWED_PROGRESS_PERCENT = 20;
const ILLUSION_DURATION_MS = 5200; // start delay + 3 steps × 1400ms

const SPRING = { type: 'spring', stiffness: 260, damping: 30 };

const slideVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 80 : -80,
    scale: 0.98
  }),
  center: { opacity: 1, x: 0, scale: 1 },
  exit: (direction) => ({
    opacity: 0,
    x: direction > 0 ? -80 : 80,
    scale: 0.98
  })
};

const staggerContainer = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const staggerItem = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

// ----- Reusable OptionCard: horizontal or vertical icon, hover lift, border transition -----
function OptionCard({
  selected,
  onClick,
  icon: Icon,
  label,
  sublabel,
  layout = 'horizontal',
  disabled,
  className = ''
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      whileHover={disabled ? {} : { y: -2 }}
      whileTap={disabled ? {} : { scale: 0.99 }}
      transition={SPRING}
      className={`
        w-full text-left rounded-2xl border-2 transition-colors duration-200 touch-manipulation
        min-h-[48px]
        ${selected
          ? 'bg-black border-black text-white shadow-lg shadow-black/10'
          : 'bg-white border-slate-100 text-black hover:border-black/20'
        }
        ${className}
      `}
    >
      <div
        className={
          layout === 'vertical'
            ? 'option-card-inner flex flex-col items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 px-3 sm:px-4'
            : 'option-card-inner flex items-center gap-3 sm:gap-4 py-2.5 sm:py-3 px-3 sm:px-4'
        }
      >
        {Icon && (
          <div
            className={`flex-shrink-0 rounded-xl flex items-center justify-center ${
              layout === 'vertical' ? 'w-10 h-10 sm:w-12 sm:h-12' : 'w-9 h-9 sm:w-11 sm:h-11'
            } ${selected ? 'bg-white/20' : 'bg-black/[0.06]'}`}
          >
            <Icon className={layout === 'vertical' ? 'w-5 h-5 sm:w-6 sm:h-6' : 'w-4 h-4 sm:w-5 sm:h-5'} />
          </div>
        )}
        <div className={`option-card-label ${layout === 'vertical' ? 'text-center' : 'flex-1 min-w-0'}`}>
          <span className="font-semibold block text-sm sm:text-lg">{label}</span>
          {sublabel && (
            <span className={`text-sm block mt-0.5 ${selected ? 'text-white/80' : 'text-black/60'}`}>{sublabel}</span>
          )}
        </div>
      </div>
    </motion.button>
  );
}

// Diagnostic questions (slides 7-21) all count as ONE effective step so the progress
// bar doesn't micro-crawl through 15 increments during the placement test.
// Effective steps: 0-6 = themselves, 7-21 = step 7, 22→8, 23→9, 24→10, 25→11
const EFFECTIVE_SLIDE_COUNT = 12; // 0-11
function getEffectiveIndex(slideIndex) {
  if (slideIndex <= 6) return slideIndex;
  if (slideIndex <= 21) return 7; // entire placement test = one step
  return slideIndex - 14; // 22→8, 23→9, 24→10, 25→11
}

function ProgressBar({ currentIndex }) {
  const effectiveIndex = getEffectiveIndex(currentIndex);
  const lastStep = EFFECTIVE_SLIDE_COUNT - 1;
  const percent =
    effectiveIndex === 0
      ? ENDOWED_PROGRESS_PERCENT
      : Math.min(100, ENDOWED_PROGRESS_PERCENT + (effectiveIndex / lastStep) * (100 - ENDOWED_PROGRESS_PERCENT));
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden bg-black/8 flex-shrink-0">
      <motion.div
        className="h-full rounded-full bg-gradient-to-r from-zinc-700 to-black"
        initial={false}
        animate={{ width: `${percent}%` }}
        transition={{ type: 'tween', duration: 0.5, ease: 'easeInOut' }}
        style={{ willChange: 'width' }}
      />
    </div>
  );
}

function SlideWrapper({ children, slideKey, direction, expandWithContent }) {
  return (
    <motion.div
      key={slideKey}
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={SPRING}
      className={
        expandWithContent
          ? 'relative flex flex-col pb-6 sm:pb-8 px-5 sm:px-12 min-h-0'
          : 'absolute inset-0 flex flex-col pb-6 sm:pb-8 px-5 sm:px-12 overflow-hidden'
      }
      style={{ willChange: 'transform' }}
    >
      <div className={expandWithContent ? 'pt-8 sm:pt-12 flex flex-col min-h-0' : 'flex-1 min-h-0 pt-8 sm:pt-12 flex flex-col overflow-y-auto hide-scrollbar'}>
        {children}
      </div>
    </motion.div>
  );
}

// --- Primary CTA button (centered, fitted)
const PrimaryButton = ({ children, onClick, disabled, className = '' }) => (
  <motion.button
    type="button"
    onClick={onClick}
    disabled={disabled}
    whileTap={{ scale: 0.98 }}
    className={`relative overflow-hidden w-fit min-h-[48px] py-3 px-8 sm:px-10 rounded-2xl bg-black text-white font-space font-bold text-sm sm:text-base touch-manipulation mx-auto flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed ${className}`}
  >
    <span
      className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
      style={{ animation: 'pb-shimmer 2.8s ease-in-out infinite' }}
      aria-hidden
    />
    {children}
  </motion.button>
);

// --- Slide 0: Purpose = Welcome + Job to Be Done (persona segmentation)
const PURPOSE_OPTIONS = [
  { id: 'exam', labelKey: 'onboarding.purpose.exam', subKey: 'onboarding.purpose.examSub', labelDefault: 'Exam Preparation', subDefault: 'Exam mocks and video lessons', icon: PenLine },
  { id: 'training', labelKey: 'onboarding.purpose.training', subKey: 'onboarding.purpose.trainingSub', labelDefault: 'German training', subDefault: 'Improve your speaking, listening, and grammar', icon: MessageCircle },
  { id: 'both', labelKey: 'onboarding.purpose.both', subKey: 'onboarding.purpose.bothSub', labelDefault: 'Both', subDefault: 'Exam + general', icon: Rocket }
];

function SlidePurpose({ value, onChange, onNext }) {
  const { t } = useTranslation();
  const isTouchDevice =
    typeof window !== 'undefined' &&
    (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);

  const handleSelect = (id) => {
    onChange(id);
    if (isTouchDevice) {
      setTimeout(() => onNext(), 220);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-shrink-0 text-center mb-4">
        <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-1.5 sm:mb-2">
          {t('onboarding.welcome.title', 'Welcome to Logami')}
        </h2>
        <p className="text-black/70 text-base sm:text-lg max-w-md mx-auto mb-3 sm:mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          {t('onboarding.welcome.subtitleV2', 'Complete your profile to get one step closer to passing your exam.')}
        </p>
      </div>
      <h3 className="font-space text-lg sm:text-xl font-black text-black tracking-tight mb-2 sm:mb-3 flex-shrink-0 text-center">
        {t('onboarding.purpose.title', 'What is your primary focus?')}
      </h3>
      <div className="flex-1 min-h-0 flex flex-col items-center">
        <motion.div className="w-full max-w-md space-y-2 pb-2" variants={staggerContainer} initial="hidden" animate="show">
          {PURPOSE_OPTIONS.map((opt) => (
            <motion.div key={opt.id} variants={staggerItem} className="w-full">
              <OptionCard
                selected={value === opt.id}
                onClick={() => handleSelect(opt.id)}
                icon={opt.icon}
                label={t(opt.labelKey, opt.labelDefault)}
                sublabel={t(opt.subKey, opt.subDefault)}
                layout="horizontal"
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
      {!isTouchDevice && (
        <div className="mt-auto flex justify-center">
          <PrimaryButton onClick={onNext} disabled={!value}>
            {t('onboarding.next', 'Next')}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

// --- Slide 2: Fork
function SlideFork({ onTakeDiagnostic, onSkip }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col flex-1 min-h-0 justify-center">
      <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-1.5 sm:mb-2">
        {t('onboarding.fork.title', "Don't waste a second on the basics")}
      </h2>
      <p className="text-black/70 text-base sm:text-lg mb-4 sm:mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
        {t('onboarding.fork.subtitle', 'Complete 15 quick questions to help us identify your strengths.')}
      </p>
      <div className="flex flex-col gap-2">
        <div className="mt-auto flex justify-center">
          <PrimaryButton onClick={onTakeDiagnostic}>
            {t('onboarding.fork.takeTest', 'Start Assessment (5 min)')}
          </PrimaryButton>
        </div>
        <button
          type="button"
          onClick={onSkip}
          className="text-black/60 underline text-sm hover:text-black touch-manipulation min-h-[48px]"
        >
          {t('onboarding.fork.skip', 'Skip placement exam')}
        </button>
      </div>
    </div>
  );
}

// --- Diagnostic: type label helper (used by PlacementDiagnosticSlide wrapper)
function getDiagnosticTypeLabel(question, t) {
  const type = question.type || 'grammar';
  return type === 'grammar'
    ? t('onboarding.types.grammar', 'Grammar')
    : type === 'styling'
      ? t('onboarding.types.styling', 'Styling')
      : t('onboarding.types.listening', 'Hören');
}

// --- Slide 18: Exam (progressive disclosure like SlideHistory: center provider, then level fades in)
const EXAM_PROVIDERS = [
  { id: 'telc', name: 'TELC' },
  { id: 'goethe', name: 'Goethe' },
  { id: 'osd', name: 'ÖSD' }
];
const EXAM_LEVELS = ['B1', 'B2', 'C1'];

/** Brief confirmation for email sign-up users who already chose exam during sign-up. */
// "3. Paper or digital": for exams with a paper version, chosen here once and
// remembered (utils/examMode). Paper is preselected.
function ExamModeStep({ provider, level, mode, onModeChange, numbered = true }) {
  const { t } = useTranslation();
  if (!supportsPaper(provider, level)) return null;
  return (
    <motion.div
      key="mode"
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={SPRING}
      className="mt-4 text-left"
    >
      <p className="text-sm text-black/60 mb-3">{numbered ? '3. ' : ''}{t('onboarding.exam.modeTitle')}</p>
      <ExamModePicker value={mode} onChange={onModeChange} compact />
    </motion.div>
  );
}

function SlideExamConfirmation({ examName, onNext, onEdit, provider, level, mode, onModeChange }) {
  const { t } = useTranslation();
  return (
    <motion.div layout className="flex flex-col flex-1 min-h-0 justify-center items-center text-center">
      <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-3 sm:mb-4">
        {t('onboarding.confirm.title', 'Just to confirm')}
      </h2>
      <p className="text-black/70 text-base sm:text-lg mb-6 sm:mb-8">
        {t('onboarding.confirm.prepareFor', 'You said you want to prepare for')}{' '}
        <strong className="text-black font-bold">{examName || t('onboarding.confirm.yourExam', 'your exam')}</strong>.
      </p>
      <div className="w-full max-w-xl mb-6">
        <ExamModeStep provider={provider} level={level} mode={mode} onModeChange={onModeChange} numbered={false} />
      </div>
      <div className="flex flex-col items-center gap-3">
        <PrimaryButton onClick={onNext}>
          {t('onboarding.confirm.yes', "Yes, that's right")}
        </PrimaryButton>
        <button
          type="button"
          onClick={onEdit}
          className="text-sm font-medium text-black/50 hover:text-black/80 transition-colors touch-manipulation min-h-[44px] px-4"
        >
          {t('onboarding.confirm.change', 'No, I want to change it')}
        </button>
      </div>
    </motion.div>
  );
}

function SlideExam({ provider, level, onProviderChange, onLevelChange, onNext, mode, onModeChange }) {
  const { t } = useTranslation();
  const bothSelected = provider && level;
  return (
    <motion.div layout className="flex flex-col flex-1 min-h-0">
      <div className={`flex flex-col flex-1 min-h-0 ${!provider ? 'justify-center' : ''}`}>
        <div>
          <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-3 sm:mb-4">
            {t('onboarding.exam.title', 'What exam are you taking?')}
          </h2>
          <p className="text-sm text-black/60 mb-3">1. {t('onboarding.exam.providerTitle', 'Select exam provider')}</p>
          <motion.div layout className="grid grid-cols-3 sm:grid-cols-3 gap-1.5 sm:gap-2 mb-6 sm:mb-10" variants={staggerContainer} initial="hidden" animate="show">
            {EXAM_PROVIDERS.map((opt) => (
              <motion.div key={opt.id} variants={staggerItem}>
                <OptionCard
                  selected={provider === opt.id}
                  onClick={() => onProviderChange(opt.id)}
                  label={opt.name}
                  layout="vertical"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
        <AnimatePresence mode="wait">
          {provider && (
            <motion.div
              key="level"
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={SPRING}
              className="min-h-0 mt-6"
            >
              <p className="text-sm text-black/60 mb-3">2. {t('onboarding.exam.levelTitle', 'Select level')}</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {EXAM_LEVELS.map((lvl) => (
                  <OptionCard
                    key={lvl}
                    selected={level === lvl}
                    onClick={() => onLevelChange(lvl)}
                    label={lvl}
                    layout="vertical"
                  />
                ))}
              </div>
              {level && <ExamModeStep provider={provider} level={level} mode={mode} onModeChange={onModeChange} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="mt-auto flex justify-center">
        <PrimaryButton onClick={onNext} disabled={!bothSelected}>
          {t('onboarding.next', 'Next')}
        </PrimaryButton>
      </div>
    </motion.div>
  );
}

// --- Slide 19: Timeline (custom calendar-style input)
function SlideTimeline({ value, onChange, onNext }) {
  const { t } = useTranslation();
  // null = not answered yet, true = has a date, false = no date yet
  const [hasTestDate, setHasTestDate] = useState(value ? true : null);
  const dateInputRef = useRef(null);

  // Auto-navigate when "Not yet" is selected, mirroring SlideHistory UX.
  // onChange is intentionally excluded from deps, it's an inline arrow that changes
  // every render and would cancel the timer before it fires.
  useEffect(() => {
    if (hasTestDate === false) {
      const timer = setTimeout(() => onNext(), 300);
      return () => clearTimeout(timer);
    }
  }, [hasTestDate, onNext]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNativeDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;
    input.focus({ preventScroll: true });
    if (typeof input.showPicker === 'function') {
      try { input.showPicker(); return; } catch (_e) { /* iOS Safari fallback */ }
    }
    input.click();
  };

  return (
    <motion.div layout className="flex flex-col flex-1 min-h-0">
      <div className={`flex flex-col flex-1 min-h-0 ${hasTestDate !== true ? 'justify-center' : ''}`}>
        <div>
          <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-3 sm:mb-4 text-center">
            {t('onboarding.timeline.question', 'Do you have a test date?')}
          </h2>
          <div className="flex justify-center gap-3 flex-wrap">
            <OptionCard
              selected={hasTestDate === true}
              onClick={() => setHasTestDate(true)}
              label={t('onboarding.timeline.yes', 'Yes')}
              layout="horizontal"
              className="w-24 sm:w-28 min-w-0 flex-shrink-0 min-h-[48px] [&_.option-card-inner]:justify-center [&_.option-card-label]:text-center"
            />
            <OptionCard
              selected={hasTestDate === false}
              onClick={() => { onChange(''); setHasTestDate(false); }}
              label={t('onboarding.timeline.notYet', 'Not yet')}
              layout="horizontal"
              className="w-24 sm:w-28 min-w-0 flex-shrink-0 min-h-[48px] [&_.option-card-inner]:justify-center [&_.option-card-label]:text-center"
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {hasTestDate === true && (
            <motion.div
              key="date-picker"
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={SPRING}
              className="mt-6"
            >
              <div className="rounded-3xl border-2 border-black/10 bg-gradient-to-b from-white to-slate-50/70 p-3 shadow-[0_10px_26px_-18px_rgba(15,23,42,0.45)]">
                <label htmlFor="onboarding-test-date" className="flex items-center gap-3 px-2 pb-2">
                  <span className="inline-flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-black/65" />
                    <span className="text-sm font-bold text-black/75">{t('onboarding.timeline.pickDate', 'Pick a date')}</span>
                  </span>
                </label>
                <div
                  className={`relative rounded-2xl border-2 transition-colors cursor-pointer ${value ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'} focus-within:border-black focus-within:bg-white`}
                  onClick={openNativeDatePicker}
                >
                  <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/45 pointer-events-none" />
                  <input
                    ref={dateInputRef}
                    id="onboarding-test-date"
                    type="date"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full min-h-[58px] pl-12 pr-4 py-2 text-base text-black font-semibold bg-transparent border-0 focus:ring-0 focus:outline-none touch-manipulation cursor-pointer [color-scheme:light]"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        {hasTestDate === true && (
          <motion.div
            key="next-btn"
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={SPRING}
            className="mt-auto flex justify-center flex-shrink-0"
          >
            <PrimaryButton onClick={onNext}>
              {t('onboarding.next', 'Next')}
            </PrimaryButton>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// --- Slide 20: History (progressive disclosure like SlideExam: question + Yes/No centered, then Result/Date animate in)
function SlideHistory({ examName, takenBefore, result, date, onTakenBeforeChange, onResultChange, onDateChange, onNext }) {
  const { t } = useTranslation();
  const historyTitle = examName
    ? t('onboarding.history.titleWithExam', 'Have you taken the {{examName}} exam before?', { examName })
    : t('onboarding.history.title', 'Have you taken this exam before?');
  
  // Show Next button only when Yes is selected.
  // If No is selected, automatically go to next page.
  const shouldShowNext = (takenBefore === true);
  
  // Auto-navigate when No is clicked
  useEffect(() => {
    if (takenBefore === false) {
      const timer = setTimeout(() => onNext(), 300); // Small delay for smooth transition
      return () => clearTimeout(timer);
    }
  }, [takenBefore, onNext]);
  
  return (
    <motion.div layout className="flex flex-col flex-1 min-h-0">
      <div className={`flex flex-col flex-1 min-h-0 ${!takenBefore ? 'justify-center' : ''}`}>
        <div>
          <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-3 sm:mb-4 text-center">
            {historyTitle}
          </h2>
          <div className="flex justify-center gap-3 flex-wrap">
            <OptionCard
              selected={takenBefore === true}
              onClick={() => onTakenBeforeChange(true)}
              label={t('onboarding.history.yes', 'Yes')}
              layout="horizontal"
              className="w-24 sm:w-28 min-w-0 flex-shrink-0 min-h-[48px] [&_.option-card-inner]:justify-center [&_.option-card-label]:text-center"
            />
            <OptionCard
              selected={takenBefore === false}
              onClick={() => onTakenBeforeChange(false)}
              label={t('onboarding.history.no', 'No')}
              layout="horizontal"
              className="w-24 sm:w-28 min-w-0 flex-shrink-0 min-h-[48px] [&_.option-card-inner]:justify-center [&_.option-card-label]:text-center"
            />
          </div>
        </div>
        <AnimatePresence mode="wait">
          {takenBefore === true && (
            <motion.div
              key="history-fields"
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={SPRING}
              className="min-h-0 mt-8 space-y-3 mb-6"
            >
              <div className="rounded-2xl border-2 border-slate-100 bg-white overflow-hidden">
                <label className="block text-sm font-medium text-black/70 px-4 pt-2 pb-1">{t('onboarding.history.result', 'Result')}</label>
                <input
                  type="text"
                  value={result || ''}
                  onChange={(e) => onResultChange(e.target.value)}
                  placeholder={t('onboarding.history.resultPlaceholder', 'e.g. Pass / 75%')}
                  className="w-full min-h-[44px] px-4 pb-2 text-base border-0 focus:ring-0 focus:outline-none bg-transparent"
                />
              </div>
              <div className="rounded-2xl border-2 border-slate-100 bg-white overflow-hidden">
                <label className="block text-sm font-medium text-black/70 px-4 pt-2 pb-1">{t('onboarding.history.date', 'Date')}</label>
                <input
                  type="date"
                  value={date || ''}
                  onChange={(e) => onDateChange(e.target.value)}
                  className="w-full min-h-[44px] px-4 pb-2 text-base border-0 focus:ring-0 focus:outline-none bg-transparent"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence mode="wait">
        {shouldShowNext && (
          <motion.div
            key="next-button"
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={SPRING}
            className="mt-auto flex justify-center flex-shrink-0"
          >
            <PrimaryButton onClick={onNext}>
              {t('onboarding.next', 'Next')}
            </PrimaryButton>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// --- Slide 22: Focus areas
const FOCUS_AREAS = [
  { id: 'writing', labelKey: 'onboarding.focus.writing', label: 'Writing' },
  { id: 'speaking', labelKey: 'onboarding.focus.speaking', label: 'Speaking' },
  { id: 'reading', labelKey: 'onboarding.focus.reading', label: 'Reading' },
  { id: 'listening', labelKey: 'onboarding.focus.listening', label: 'Listening' },
  { id: 'grammar', labelKey: 'onboarding.focus.grammar', label: 'Grammar' },
  { id: 'vocabulary', labelKey: 'onboarding.focus.vocabulary', label: 'Vocabulary' }
];

// --- Slide 21: Focus areas
function SlideFocusAreas({ selected, onToggle, onNext }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-1.5 sm:mb-2 flex-shrink-0">
        {t('onboarding.focus.title', 'Focus areas')}
      </h2>
      <p className="text-black/60 text-base mb-3 flex-shrink-0">{t('onboarding.focus.subtitle', 'Select all that apply.')}</p>
      <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar pb-2">
        <div className="flex flex-wrap gap-2 content-start">
          {FOCUS_AREAS.map((area) => (
            <motion.div key={area.id} whileHover={{ y: -2 }} whileTap={{ scale: 0.99 }}>
              <button
                type="button"
                onClick={() => onToggle(area.id)}
                className={`min-h-[44px] px-3 sm:px-4 rounded-2xl border-2 font-medium text-sm sm:text-base touch-manipulation transition-colors ${
                  selected.includes(area.id) ? 'bg-black border-black text-white shadow-lg shadow-black/10' : 'bg-white border-slate-100 hover:border-black/20'
                }`}
              >
                {t(area.labelKey, area.label)}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mt-auto flex justify-center">
        <PrimaryButton onClick={onNext}>
          {t('onboarding.next', 'Next')}
        </PrimaryButton>
      </div>
    </div>
  );
}

// --- Slide 22: Referral (Where did you hear about us?)
const REFERRAL_OPTIONS = [
  { id: 'google', labelKey: 'onboarding.referral.google', labelDefault: 'Google' },
  { id: 'friends', labelKey: 'onboarding.referral.friends', labelDefault: 'Friends / Word of Mouth' },
  { id: 'youtube', labelKey: 'onboarding.referral.youtube', labelDefault: 'YouTube' },
  { id: 'social', labelKey: 'onboarding.referral.social', labelDefault: 'Social media (Instagram / TikTok)' },
  { id: 'flyers', labelKey: 'onboarding.referral.flyers', labelDefault: 'Flyers / Posters' }
];

function SlideReferral({ value, onChange, onNext }) {
  const { t } = useTranslation();
  const selectedIds = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-3 sm:mb-4 flex-shrink-0">
        {t('onboarding.referral.title', 'Where did you hear about us?')}
      </h2>
      <p className="text-sm text-black/60 mb-3 flex-shrink-0">
        {t('onboarding.referral.subtitle', 'You can pick more than one.')}
      </p>
      <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pb-2">
          {REFERRAL_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.id}
              selected={selectedIds.includes(opt.id)}
              onClick={() => toggle(opt.id)}
              label={t(opt.labelKey, opt.labelDefault)}
              layout="horizontal"
              className="min-h-[48px]"
            />
          ))}
        </div>
      </div>
      <div className="mt-auto flex justify-center">
        <PrimaryButton onClick={onNext} disabled={selectedIds.length === 0}>
          {t('onboarding.next', 'Next')}
        </PrimaryButton>
      </div>
    </div>
  );
}

// --- Slide 17 (after diagnostic): Interstitial celebration (peak-end rule, cognitive load)
function SlideDiagnosticComplete({ level, score, onNext }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col flex-1 min-h-0 items-center justify-center text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        className="w-18 h-18 sm:w-24 sm:h-24 bg-black rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-xl shadow-black/10"
      >
        <Trophy className="w-9 h-9 sm:w-12 sm:h-12 text-white" />
      </motion.div>

      <motion.h2
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-2 sm:mb-3"
      >
        {t('onboarding.diagnosticComplete.title', 'Test Complete!')}
      </motion.h2>

      <motion.p
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-black/70 text-base sm:text-lg max-w-sm mb-6 sm:mb-8"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {t(
          'onboarding.diagnosticComplete.subtitle',
          "Awesome work. We've analyzed your skills and estimated your current level at "
        )}{' '}
        <strong className="text-black">{level || 'B1'}</strong>.{' '}
        {t('onboarding.diagnosticComplete.subtitle2', " Now, let's set your goals.")}
      </motion.p>

      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-4"
      >
        <PrimaryButton onClick={onNext}>
          {t('onboarding.diagnosticComplete.cta', 'Set My Goals')}
        </PrimaryButton>
      </motion.div>
    </div>
  );
}

// --- Slide 24: Illusion of Labor (sequential reveal: only show steps as they become active)
const ILLUSION_STEP_MS = 1400;
const ILLUSION_START_DELAY_MS = 500;

function SlideIllusionOfLabor({ examName }) {
  const { t } = useTranslation();
  const [completedCount, setCompletedCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  const statuses = [
    t('onboarding.illusion.status1', 'Pinpointing your unique strengths..'),
    t('onboarding.illusion.status2', 'Matching your skills to {{examName}} requirements..', { examName: examName || 'exam' }),
    t('onboarding.illusion.status3', 'Finalizing your personalized study plan')
  ];

  // Brief delay so the first line + spinner is visible before the first completion
  useEffect(() => {
    const startTimer = setTimeout(() => setHasStarted(true), ILLUSION_START_DELAY_MS);
    return () => clearTimeout(startTimer);
  }, []);

  useEffect(() => {
    if (!hasStarted || completedCount >= statuses.length) return;
    const timer = setTimeout(() => setCompletedCount((c) => c + 1), ILLUSION_STEP_MS);
    return () => clearTimeout(timer);
  }, [hasStarted, completedCount, statuses.length]);

  return (
    <div className="flex flex-col flex-1 min-h-0 justify-center">
      <ul className="space-y-5 w-full max-w-md">
        {statuses.slice(0, completedCount + 1).map((status, idx) => {
          const i = idx;
          const label = status;
          const isDone = i < completedCount;
          const isActive = i === completedCount;
          return (
            <motion.li
              key={i}
              initial={{ opacity: 0, y: 12, filter: 'blur(8px)' }}
              animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }}
              transition={{
                opacity: { duration: 0.4 },
                y: { type: 'spring', stiffness: 320, damping: 32 },
                filter: { duration: 0.45, ease: 'easeOut' }
              }}
              className="flex items-center gap-3"
            >
              <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                <AnimatePresence mode="wait">
                  {isDone ? (
                    <motion.span
                      key="check"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      transition={SPRING}
                      className="text-black"
                    >
                      <CheckCircle2 className="w-6 h-6" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="spinner"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center justify-center"
                    >
                      <LogamiLogoSpinner size="lg" decorative={true} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </span>
              <span
                className={`font-medium transition-colors duration-300 ${
                  isDone ? 'text-black/50' : 'text-black/90'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {label}
              </span>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

// --- Slide 25: Aha (slide-up reveal + Start Plan CTA)
const ahaStagger = { staggerChildren: 0.08, delayChildren: 0.05 };
const ahaSlideUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

function SlideAha({ payload, onClose }) {
  const { t } = useTranslation();
  const examName = payload?.examName || 'Your exam';
  const testDate = payload?.testDate || '';
  const weakestSubject = payload?.weakestSubject || 'Grammar';

  useEffect(() => {
    soundManager.play(SOUNDS.SUCCESS);
  }, []);

  return (
    <div className="flex flex-col flex-1 min-h-0 items-center justify-center text-center py-8">
      <motion.div
        variants={ahaStagger}
        initial="hidden"
        animate="show"
        transition={{ type: 'spring', stiffness: 260, damping: 30 }}
        className="flex flex-col items-center"
      >
        <motion.div
          variants={ahaSlideUp}
          transition={SPRING}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-black flex items-center justify-center mb-4 sm:mb-5 shadow-xl shadow-black/20 flex-shrink-0"
        >
          <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
        </motion.div>
        <motion.h2
          variants={ahaSlideUp}
          transition={SPRING}
          className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-2"
        >
          {t('onboarding.aha.title', 'Your plan is ready')}
        </motion.h2>
        <motion.p
          variants={ahaSlideUp}
          transition={SPRING}
          className="text-black/70 text-base sm:text-lg mb-1"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {examName}{testDate ? ` • ${testDate}` : ''}
        </motion.p>
        <motion.p
          variants={ahaSlideUp}
          transition={SPRING}
          className="text-black/60 text-sm sm:text-base mb-6 sm:mb-8"
        >
          {t('onboarding.aha.focus', 'We\'ll focus on')} <strong>{weakestSubject}</strong>
        </motion.p>
        <motion.div variants={ahaSlideUp} transition={SPRING} className="w-full flex justify-center">
          <motion.button
            type="button"
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            animate={{
              boxShadow: [
                '0 0 0 0 rgba(0,0,0,0.15)',
                '0 0 0 10px rgba(0,0,0,0)',
                '0 0 0 0 rgba(0,0,0,0)'
              ]
            }}
            transition={{
              boxShadow: { duration: 1.8, repeat: Infinity, ease: 'easeOut' }
            }}
            className="min-h-[48px] sm:min-h-[52px] px-8 sm:px-10 rounded-2xl bg-black text-white font-space font-bold text-sm sm:text-base touch-manipulation shadow-lg shadow-black/20"
          >
            {t('onboarding.aha.startPlan', "Let's Begin")}
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
}

const ONBOARDING_PRICING_PLANS = [
  {
    id: 'free',
    price: '€0',
    nameKey: 'pricing.lineup.free.name',
    cycleKey: 'pricing.plans.cycles.once',
  },
  {
    id: 'quarterly',
    price: getPlanPriceLabel('quarterly'),
    nameKey: 'pricing.lineup.quarterly.name',
    cycleKey: 'pricing.plans.cycles.quarter',
    isPopular: true
  },
  {
    id: 'premium',
    price: getPlanPriceLabel('premium'),
    nameKey: 'pricing.lineup.premium.name',
    cycleKey: 'pricing.lineup.premium.cycle',
    badgeKey: 'pricing.lineup.premium.badge'
  }
];

// --- Slide 23: Pricing selection (before illusion/computing animation)
function SlidePricing({ selectedPlanId, onSelectPlan, onContinue }) {
  const { t } = useTranslation();
  const { getPriceLabel } = useRegionalPricing();
  const pricingPlans = ONBOARDING_PRICING_PLANS.map((plan) => (
    plan.id === 'free' ? plan : { ...plan, price: getPriceLabel(plan.id) }
  ));
  // null / '' = nothing selected yet; no default pre-selection
  const selectedPlan = pricingPlans.find((plan) => plan.id === selectedPlanId) || null;
  const hasSelection = Boolean(selectedPlan);

  // On touch/mobile: selecting a paid plan immediately continues (no extra tap needed)
  const isTouchDevice =
    typeof window !== 'undefined' &&
    (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);

  const handleSelect = (planId) => {
    onSelectPlan(planId);
    if (isTouchDevice) {
      // Pass planId explicitly to avoid stale closure, state update may not have committed yet
      setTimeout(() => onContinue(planId), 220);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="flex flex-col flex-1 min-h-0">
        <motion.div variants={staggerItem} className="text-center mb-4 sm:mb-5 flex-shrink-0">
          <h2 className="font-space text-xl sm:text-3xl font-black text-black tracking-tighter mb-2">
            {t('onboarding.pricing.title', 'Choose your plan')}
          </h2>
          <p className="text-black/70 text-sm sm:text-base max-w-xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('onboarding.pricing.subtitle', 'Start free or unlock full access to pass your exam.')}
          </p>
        </motion.div>

        <motion.div variants={staggerItem} className="flex-1 min-h-0 overflow-y-auto hide-scrollbar pb-2">
          {/* One column on phones, all three side by side from tablet width:
              with two columns Premium sat alone on a second row. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-3 pt-3">
            {pricingPlans.map((plan) => {
              const isSelected = selectedPlan?.id === plan.id;
              const benefits = t(`pricing.lineup.${plan.id}.benefits`, { returnObjects: true });
              const benefitList = Array.isArray(benefits) ? benefits : [];

              return (
                <motion.button
                  key={plan.id}
                  type="button"
                  onClick={() => handleSelect(plan.id)}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.99 }}
                  transition={SPRING}
                  className={`relative flex flex-col justify-start text-left rounded-2xl border-2 p-4 sm:p-5 touch-manipulation transition-colors duration-200 min-h-[48px] ${
                    isSelected
                      ? 'bg-black border-black text-white shadow-lg shadow-black/15'
                      : plan.isPopular
                        ? 'bg-white border-black text-black shadow-md shadow-black/10'
                        : 'bg-white border-slate-100 text-black hover:border-black/20'
                  }`}
                >
                  {/* Badges sit on the card's top edge: in the corner they covered
                      the plan name when three narrow cards share a row. */}
                  {plan.isPopular ? (
                    <span className="absolute -top-2.5 left-4 max-w-[calc(100%-2rem)] truncate text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                      {t('pricing.plans.labels.popular', 'Most Popular')}
                    </span>
                  ) : plan.badgeKey ? (
                    <span className="absolute -top-2.5 left-4 max-w-[calc(100%-2rem)] truncate text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                      {t(plan.badgeKey)}
                    </span>
                  ) : null}

                  <div className="mb-2.5">
                    <h3 className="font-space text-base sm:text-lg font-black tracking-tight">
                      {t(plan.nameKey)}
                    </h3>
                  </div>

                  <div className="mb-3">
                    <p className="font-space text-2xl sm:text-3xl font-black leading-none flex items-end gap-1.5">
                      <span>{plan.price}</span>
                    </p>
                    <p className={`text-xs uppercase tracking-wider mt-1 ${isSelected ? 'text-white/60' : 'text-black/50'}`}>
                      / {t(plan.cycleKey)}
                    </p>
                  </div>

                  <ul className="space-y-1.5">
                    {benefitList.map((benefit, idx) => (
                      <li key={`${plan.id}-benefit-${idx}`} className={`flex items-start gap-1.5 text-xs sm:text-sm leading-snug ${isSelected ? 'text-white/80' : 'text-black/70'}`}>
                        <span className={`flex-shrink-0 font-black mt-px ${isSelected ? 'text-white/60' : 'text-black/50'}`}>, </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  <p className={`mt-3 text-[10px] font-semibold ${isSelected ? 'text-white/35' : 'text-black/30'}`}>
                    {t(`pricing.lineup.${plan.id}.trustLine`)}
                  </p>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>

      <div className="mt-3 sm:mt-4 flex flex-col items-center gap-2.5 sm:gap-3 flex-shrink-0">
        {hasSelection && (
          <p className="text-xs text-black/55 text-center">
            {t('onboarding.pricing.selected', 'Selected')}: <span className="font-semibold text-black">{t(selectedPlan.nameKey)}</span>
          </p>
        )}
        <PrimaryButton onClick={() => onContinue()} disabled={!hasSelection}>
          {t('onboarding.pricing.continue', 'Continue')}
        </PrimaryButton>
      </div>
    </div>
  );
}

function Toast({ message, visible }) {
  if (!message) return null;
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[210] px-4 py-2 rounded-xl bg-black text-white text-sm font-medium shadow-lg"
        >
          {message}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function getExamName(provider, level) {
  const names = { telc: 'TELC', goethe: 'Goethe', osd: 'ÖSD' };
  return `${names[provider] || provider} ${level || ''}`.trim();
}

function getOnboardingStepName(index) {
  if (index >= 7 && index <= 21) return `placement_question_${index - 6}`;

  const stepNames = {
    0: 'purpose_selection',
    1: 'exam_selection',
    2: 'exam_date_selection',
    3: 'exam_history',
    4: 'focus_areas',
    5: 'referral_source',
    6: 'placement_test_choice',
    22: 'placement_results',
    23: 'plan_selection',
    24: 'plan_generation',
    25: 'plan_ready'
  };

  return stepNames[index] || `step_${index + 1}`;
}

export default function OnboardingModal({ open, onComplete, user, asPage = false, preSelectedExam = null, onRequestCheckout = null, initialIndex = null, initialResponses = null }) {
  const { t } = useTranslation();
  // For variant_b users, skip the placement test (slides 6–22) entirely.
  // The study plan works fine without it, placementAnswers is optional in generatePersonalizedPlan().
  const isVariantB = getLandingPageVariant() === 'variant_b';
  const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
  const [direction, setDirection] = useState(1);
  const defaultResponses = {
    purpose: '',
    tookDiagnostic: false,
    placementAnswers: [],
    placementScore: null,
    placementLevel: null,
    weakestSubject: null,
    examProvider: preSelectedExam?.provider ?? '',
    examLevel: preSelectedExam?.level ?? '',
    examMode: getExamModePreference(),
    testDate: '',
    takenBefore: null,
    previousResult: '',
    previousDate: '',
    focusAreas: [],
    heardFrom: [],
    pricingPlanId: ''
  };
  const [responses, setResponses] = useState(initialResponses ? { ...defaultResponses, ...initialResponses } : defaultResponses);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [showIllusion, setShowIllusion] = useState(false);
  const [resultMessageByQuestionIndex, setResultMessageByQuestionIndex] = useState({});
  const previousIndexRef = useRef(initialIndex ?? 0);

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2000);
  }, []);

  const CORRECT_MSG_KEYS = [
    'onboarding.diagnostic.correct.1',
    'onboarding.diagnostic.correct.2',
    'onboarding.diagnostic.correct.3',
    'onboarding.diagnostic.correct.4',
    'onboarding.diagnostic.correct.5'
  ];
  const INCORRECT_MSG_KEYS = [
    'onboarding.diagnostic.incorrect.1',
    'onboarding.diagnostic.incorrect.2',
    'onboarding.diagnostic.incorrect.3',
    'onboarding.diagnostic.incorrect.4',
    'onboarding.diagnostic.incorrect.5'
  ];
  const pickResultMessage = useCallback((correct) => {
    const keys = correct ? CORRECT_MSG_KEYS : INCORRECT_MSG_KEYS;
    const key = keys[Math.floor(Math.random() * keys.length)];
    return t(key, correct ? 'Correct! Keep going.' : "Not quite, we'll focus on that.");
  }, [t]);

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((i) => Math.min(i + 1, SLIDE_COUNT - 1));
  }, []);

  // overridePlanId: passed by the mobile auto-advance path to avoid stale closure on pricingPlanId
  const handlePricingContinue = useCallback((overridePlanId) => {
    const planId = overridePlanId ?? responses.pricingPlanId;
    trackOnboardingStepCompleted({
      stepName: getOnboardingStepName(23),
      stepNumber: 24,
      userId: user?.uid
    });

    if (!planId || planId === 'free') {
      goNext();
    } else if (onRequestCheckout) {
      onRequestCheckout(planId, responses);
    } else {
      goNext();
    }
  }, [responses, goNext, onRequestCheckout, user?.uid]);

  const goBack = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((i) => {
      // variant_b skips the fork + diagnostic entirely: Pricing → Referral (5)
      if (i === 23 && isVariantB) return 5;
      // Normal skip-diagnostic path: Pricing → Fork (6)
      if (i === 23 && !responses.tookDiagnostic) return 6;
      return Math.max(0, i - 1);
    });
  }, [responses.tookDiagnostic, isVariantB]);

  // Prefill exam when email sign-up has already chosen (e.g. preSelectedExam loaded async)
  useEffect(() => {
    if (!preSelectedExam?.provider || !preSelectedExam?.level) return;
    setResponses((r) => (
      (r.examProvider === preSelectedExam.provider && r.examLevel === preSelectedExam.level)
        ? r
        : { ...r, examProvider: preSelectedExam.provider, examLevel: preSelectedExam.level }
    ));
  }, [preSelectedExam?.provider, preSelectedExam?.level]);

  // Advance from illusion (24) to "Let's Begin" (25) after delay. Don't depend on showIllusion
  // so the effect doesn't re-run and clear the timeout when we set showIllusion(true).
  useEffect(() => {
    if (currentIndex !== 24) return;
    setShowIllusion(true);
    const timer = setTimeout(() => setCurrentIndex(25), ILLUSION_DURATION_MS);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  const handleForkTake = () => {
    setResponses((r) => ({ ...r, tookDiagnostic: true }));
    goNext();
  };
  const handleForkSkip = () => {
    setResponses((r) => ({ ...r, tookDiagnostic: false }));
    setDirection(1);
    setCurrentIndex(23); // skip diagnostic, go straight to pricing
  };

  const handleDiagnosticAnswer = (questionIndex, value) => {
    const q = PLACEMENT_QUESTIONS[questionIndex];
    const correct = q.exerciseType === 'multipleChoice' ? value === getCorrectIndex(q) : value === true;
    setResultMessageByQuestionIndex((prev) => ({ ...prev, [questionIndex]: pickResultMessage(correct) }));
    setResponses((r) => ({
      ...r,
      placementAnswers: [...r.placementAnswers.slice(0, questionIndex), value, ...r.placementAnswers.slice(questionIndex + 1)]
    }));
  };

  const handleDiagnosticNext = (questionIndex) => {
    const newAnswers = [...responses.placementAnswers.slice(0, questionIndex + 1)];
    const allFirstThreeCorrect = questionIndex === 2 && newAnswers.length === 3 && newAnswers.every((a, i) => {
      const q = PLACEMENT_QUESTIONS[i];
      return q.exerciseType === 'multipleChoice' ? a === getCorrectIndex(q) : a === true;
    });
          if (allFirstThreeCorrect) {
            showToast(t('onboarding.diagnostic.skipAdvanced', 'Impressive! Skipping to advanced questions to save you time.'));
            setResponses((r) => ({ ...r, placementAnswers: [...newAnswers, ...Array(Math.max(0, FIRST_ADVANCED_QUESTION_INDEX - 3)).fill(null)] }));
            setCurrentIndex(7 + FIRST_ADVANCED_QUESTION_INDEX);
            return;
          }
          if (questionIndex < PLACEMENT_QUESTIONS.length - 1) goNext();
          else setCurrentIndex(22);
  };

  useEffect(() => {
    if (currentIndex === 22 && responses.tookDiagnostic && responses.placementAnswers?.length > 0) {
      const score = PLACEMENT_QUESTIONS.filter((q, i) => {
        const a = responses.placementAnswers[i];
        if (a == null) return false;
        return q.exerciseType === 'multipleChoice' ? a === getCorrectIndex(q) : a === true;
      }).length;
      const totalAnswered = responses.placementAnswers.filter((a) => a != null).length;
      const level = getPlacementLevel(score, totalAnswered);
      const weakest = getWeakestSubject(PLACEMENT_QUESTIONS, responses.placementAnswers);
      setResponses((r) => ({ ...r, placementScore: score, placementLevel: level, weakestSubject: weakest }));
    }
  }, [currentIndex, responses.tookDiagnostic, responses.placementAnswers]);

  useEffect(() => {
    const previousIndex = previousIndexRef.current;

    if (currentIndex > previousIndex) {
      trackOnboardingStepCompleted({
        stepName: getOnboardingStepName(previousIndex),
        stepNumber: previousIndex + 1,
        userId: user?.uid
      });
    }

    previousIndexRef.current = currentIndex;
  }, [currentIndex, user?.uid]);

  useEffect(() => {
    if (currentIndex !== 23) return;
    trackPricingView({ source: 'onboarding_pricing' });
  }, [currentIndex]);

  const handleComplete = useCallback(() => {
    const provider = responses.examProvider || 'telc';
    const level = (responses.examLevel || 'B2').toUpperCase();
    const examId = `${provider}-${level.toLowerCase()}`;
    const examName = getExamName(provider, level);
    const pricingPlanId = responses.pricingPlanId || 'free';
    const selectedPricingPlan = ONBOARDING_PRICING_PLANS.find((plan) => plan.id === pricingPlanId) || ONBOARDING_PRICING_PLANS[0];
    const payload = {
      purpose: responses.purpose,
      tookDiagnostic: responses.tookDiagnostic,
      placementAnswers: responses.placementAnswers || [],
      placementScore: responses.placementScore ?? null,
      placementLevel: responses.placementLevel,
      weakestSubject: responses.weakestSubject,
      examId,
      examProvider: provider,
      level,
      examName,
      testDate: responses.testDate || null,
      takenBefore: responses.takenBefore,
      previousResult: responses.previousResult || null,
      previousDate: responses.previousDate || null,
      focusAreas: responses.focusAreas,
      heardFrom: Array.isArray(responses.heardFrom)
        ? responses.heardFrom
        : String(responses.heardFrom || '').split(',').map((item) => item.trim()).filter(Boolean),
      pricingPlanId,
      pricingPlanName: t(selectedPricingPlan.nameKey),
      pricingAction: 'selected-plan'
    };
    onComplete(payload);
  }, [responses, onComplete, t]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      const target = e.target;
      const isFormField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
      if (e.key === 'Enter') {
        if (isFormField) return;
        e.preventDefault();
        if (currentIndex === 23 || currentIndex === 24 || currentIndex === 25) return;
        goNext();
      } else if (e.key === 'Backspace') {
        if (isFormField) return;
        e.preventDefault();
        if (currentIndex > 0 && currentIndex !== 24 && currentIndex !== 25) goBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, goNext, goBack]);

  if (!open) return null;

  const isIllusionSlide = currentIndex === 24 && showIllusion;
  const isAhaSlide = currentIndex === 25;
  const showBack = currentIndex > 0 && !isIllusionSlide && !isAhaSlide;
  const isDiagnosticSlide = responses.tookDiagnostic && currentIndex >= 7 && currentIndex <= 21;
  const diagnosticQuestionIndex = currentIndex - 7;
  const hasDiagnosticFeedback = isDiagnosticSlide && diagnosticQuestionIndex < PLACEMENT_QUESTIONS.length && responses.placementAnswers[diagnosticQuestionIndex] != null && PLACEMENT_QUESTIONS[diagnosticQuestionIndex]?.feedback;
  const isPurposeSlide = currentIndex === 0;
  const isPricingSlide = currentIndex === 23;
  const isSentenceBuildingSlide = isDiagnosticSlide && diagnosticQuestionIndex < PLACEMENT_QUESTIONS.length && PLACEMENT_QUESTIONS[diagnosticQuestionIndex]?.exerciseType === 'sentenceBuilding';
  const modalExpandsWithContent = isPurposeSlide || hasDiagnosticFeedback || isSentenceBuildingSlide || isPricingSlide;

  // Remembered on this device now; OnboardingPage saves it to the profile.
  const chooseExamMode = (mode) => {
    setResponses((r) => ({ ...r, examMode: mode }));
    setExamModePreference(mode);
  };

  let slideContent = null;
  if (currentIndex === 0) slideContent = <SlidePurpose value={responses.purpose} onChange={(v) => setResponses((r) => ({ ...r, purpose: v }))} onNext={goNext} />;
  else if (currentIndex === 1) {
    const examProviderForConfirm = responses.examProvider || preSelectedExam?.provider;
    const examLevelForConfirm = responses.examLevel || preSelectedExam?.level;
    if (preSelectedExam && examProviderForConfirm && examLevelForConfirm && !responses._editingExam)
      slideContent = (
        <SlideExamConfirmation
          examName={getExamName(examProviderForConfirm, examLevelForConfirm)}
          onNext={goNext}
          onEdit={() => setResponses((r) => ({ ...r, _editingExam: true, examProvider: '', examLevel: '' }))}
          provider={examProviderForConfirm}
          level={examLevelForConfirm}
          mode={responses.examMode}
          onModeChange={chooseExamMode}
        />
      );
    else
      slideContent = (
        <SlideExam
          provider={responses.examProvider}
          level={responses.examLevel}
          onProviderChange={(v) => setResponses((r) => ({ ...r, examProvider: v }))}
          onLevelChange={(v) => setResponses((r) => ({ ...r, examLevel: v }))}
          onNext={goNext}
          mode={responses.examMode}
          onModeChange={chooseExamMode}
        />
      );
  }
  else if (currentIndex === 2) slideContent = <SlideTimeline value={responses.testDate} onChange={(v) => setResponses((r) => ({ ...r, testDate: v }))} onNext={goNext} />;
  else if (currentIndex === 3) slideContent = (
    <SlideHistory
      examName={getExamName(responses.examProvider, responses.examLevel)}
      takenBefore={responses.takenBefore}
      result={responses.previousResult}
      date={responses.previousDate}
      onTakenBeforeChange={(v) => setResponses((r) => ({
        ...r,
        takenBefore: v,
        ...(v === false ? { previousResult: '', previousDate: '' } : {})
      }))}
      onResultChange={(v) => setResponses((r) => ({ ...r, previousResult: v }))}
      onDateChange={(v) => setResponses((r) => ({ ...r, previousDate: v }))}
      onNext={goNext}
    />
  );
  else if (currentIndex === 4) slideContent = (
    <SlideFocusAreas
      selected={responses.focusAreas}
      onToggle={(id) => setResponses((r) => ({ ...r, focusAreas: r.focusAreas.includes(id) ? r.focusAreas.filter((x) => x !== id) : [...r.focusAreas, id] }))}
      onNext={goNext}
    />
  );
  else if (currentIndex === 5) slideContent = (
    <SlideReferral
      value={responses.heardFrom}
      onChange={(v) => setResponses((r) => ({ ...r, heardFrom: v }))}
      onNext={isVariantB ? () => {
        // variant_b: silently skip the placement fork + all diagnostic slides,
        // exactly as if the user had pressed "Skip" on the fork slide.
        setResponses((r) => ({ ...r, tookDiagnostic: false }));
        setDirection(1);
        setCurrentIndex(23);
      } : goNext}
    />
  );
  else if (currentIndex === 6) slideContent = <SlideFork onTakeDiagnostic={handleForkTake} onSkip={handleForkSkip} />;
  else if (isDiagnosticSlide && diagnosticQuestionIndex < PLACEMENT_QUESTIONS.length) {
    const q = PLACEMENT_QUESTIONS[diagnosticQuestionIndex];
    const typeIcons = { grammar: Type, styling: Layers, listening: Headphones };
    const hasAnswered = responses.placementAnswers[diagnosticQuestionIndex] != null;
    const correctForFeedback = q.exerciseType === 'multipleChoice'
      ? responses.placementAnswers[diagnosticQuestionIndex] === getCorrectIndex(q)
      : responses.placementAnswers[diagnosticQuestionIndex] === true;
    const correctAnswerText = q.exerciseType === 'multipleChoice' && q.options ? q.options[getCorrectIndex(q)] : (q.correctAnswer || q.correctStimulus);
    slideContent = (
      <div className="flex flex-col flex-1 min-h-0">
        <PlacementDiagnosticSlide
          question={q}
          questionIndex={diagnosticQuestionIndex}
          totalQuestions={PLACEMENT_QUESTIONS.length}
          placementAnswer={responses.placementAnswers[diagnosticQuestionIndex]}
          onSelect={handleDiagnosticAnswer}
          typeLabel={getDiagnosticTypeLabel(q, t)}
          TypeIcon={typeIcons[q.type] || Type}
        />
        {/* Result message + expandable feedback: shown after answer, inside the modal */}
        <AnimatePresence>
          {hasAnswered && (
            <motion.div
              layout
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="mt-4 overflow-hidden"
            >
              <div className="p-4 rounded-2xl border-2 bg-black/[0.04] border-black/10">
                {resultMessageByQuestionIndex[diagnosticQuestionIndex] && (
                  <p className={`text-sm font-medium mb-3 ${correctForFeedback ? 'text-emerald-700' : 'text-amber-800'}`}>
                    {resultMessageByQuestionIndex[diagnosticQuestionIndex]}
                  </p>
                )}
                {q.feedback && (
                  <>
                    <p className="text-xs font-semibold text-black/50 uppercase tracking-wider mb-1">
                      {t('onboarding.diagnostic.feedbackLabel', 'Feedback')}
                    </p>
                    <p className="text-sm text-black/80 leading-snug" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {q.feedback}
                    </p>
                  </>
                )}
                {!correctForFeedback && correctAnswerText && (
                  <p className="mt-2 text-sm font-medium text-black">
                    {t('onboarding.diagnostic.correctAnswer', 'Correct answer')}: {correctAnswerText}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {hasAnswered && (
          <div className="mt-4 flex justify-center flex-shrink-0">
            <PrimaryButton onClick={() => handleDiagnosticNext(diagnosticQuestionIndex)}>
              {diagnosticQuestionIndex < PLACEMENT_QUESTIONS.length - 1 ? t('onboarding.next', 'Next') : t('onboarding.diagnostic.nextToExam', 'Next')}
            </PrimaryButton>
          </div>
        )}
      </div>
    );
  } else if (currentIndex === 22) slideContent = (
    <SlideDiagnosticComplete
      level={responses.placementLevel}
      score={responses.placementScore}
      onNext={goNext}
    />
  );
  else if (currentIndex === 23) slideContent = (
    <SlidePricing
      selectedPlanId={responses.pricingPlanId}
      onSelectPlan={(planId) => {
        setResponses((r) => ({ ...r, pricingPlanId: planId }));
        trackPlanSelected({ planId, context: 'onboarding', userId: user?.uid });
      }}
      onContinue={handlePricingContinue}
    />
  );
  else if (currentIndex === 24) slideContent = <SlideIllusionOfLabor examName={getExamName(responses.examProvider, responses.examLevel)} />;
  else if (currentIndex === 25) slideContent = (
    <SlideAha
      payload={{
        examName: getExamName(responses.examProvider, responses.examLevel),
        testDate: responses.testDate,
        weakestSubject: responses.weakestSubject
      }}
      onClose={handleComplete}
    />
  );

  if (!open) return null;

  return (
    <>
      <style>{`@keyframes pb-shimmer { 0%,100% { transform:translateX(-100%); } 60% { transform:translateX(220%); } }`}</style>
      <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center pt-2.5 px-2.5 pb-[max(10px,env(safe-area-inset-bottom))] sm:p-6" aria-modal="true" role="dialog" aria-label={t('onboarding.ariaLabel', 'Onboarding')}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className={`relative w-full overflow-hidden flex flex-col bg-[radial-gradient(at_top_right,_var(--tw-gradient-stops))] from-slate-50 via-white to-white border border-black/[0.05] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] rounded-2xl sm:rounded-3xl transition-[max-width] duration-300 ${
            isPricingSlide ? 'max-w-4xl' : 'max-w-2xl'
          } ${
            modalExpandsWithContent ? 'h-auto min-h-[70dvh] sm:min-h-[520px] max-h-[95dvh] sm:max-h-[90vh]' : 'min-h-[70dvh] sm:min-h-[520px] max-h-[95dvh] sm:max-h-[90vh]'
          }`}
        >
          {/* Persistent progress bar: fixed header, does not overlap back button */}
          <header className="flex-shrink-0 pt-4 sm:pt-6 px-5 sm:px-8">
            <div className="ml-12 sm:ml-12">
              <ProgressBar currentIndex={currentIndex} />
            </div>
          </header>
          {!isAhaSlide && !isIllusionSlide && showBack && (
            <motion.button
              type="button"
              onClick={goBack}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="absolute left-4 top-3 sm:left-6 sm:top-6 z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/5 flex items-center justify-center touch-manipulation hover:bg-black/10 border border-black/[0.05]"
              aria-label={t('onboarding.back', 'Back')}
            >
              <ChevronLeft className="w-5 h-5 text-black" />
            </motion.button>
          )}
          <div
            className={`relative min-h-0 ${
              modalExpandsWithContent
                ? 'overflow-y-auto max-h-[calc(95vh-80px)] sm:max-h-[calc(90vh-100px)]'
                : 'flex-1 overflow-hidden'
            }`}
          >
            <AnimatePresence mode="wait" initial={false} custom={direction}>
              {slideContent && (
                <SlideWrapper key={currentIndex} slideKey={currentIndex} direction={direction} expandWithContent={modalExpandsWithContent}>
                  {slideContent}
                </SlideWrapper>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
      <Toast message={toastMessage} visible={toastVisible} />
    </>
  );
}
